import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle Hilfe-Tour fuer den Dialog "Qualifikation hinzufuegen"
 * (`add-edit-skill.component`). Gestartet ueber den `<app-dialog-help-button>`
 * im Dialog-Header (Pattern analog `EMPLOYEE_ADD_HELP_TOUR`).
 *
 * Flaches Formular (kein `mat-accordion`) → KEIN `panelIndex`, keine Cues
 * (ein Feld pro Schritt). Setup-getriebene `SKILL_ADD_CONTEXTUAL_TOUR` +
 * deren `onboarding:skill.*`-Events bleiben unangetastet.
 *
 * Kernbotschaft: Name + Icon sind das Etikett, die Abteilungszuordnung ist
 * Organisation — die eigentliche Wirkung der Qualifikation ist das
 * Schicht-Matching (erklaert im Welcome + auf der Org-Seiten-Tour).
 */
const DIALOG_CTX = { attachToDialog: true, autoCloseOnDialogClose: true };

export const SKILL_ADD_HELP_TOUR: TourConfig = {
  id: TourFeature.SkillAddHelp,
  version: 1,
  title: 'SKILL_ADD_HELP_TOUR_TITLE',
  description: 'SKILL_ADD_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Organisations-Skills-Seite navigieren, dann den
  // Add-Button klicken. Der Anchor `add-skill-button` wird in
  // `organization-header.component.ts:243` dynamisch je Tab gebunden — daher
  // ist die genaue Sub-Route Pflicht.
  launchContext: {
    route: '/organization/skills',
    opens: { openerAnchor: AnchorId.AddSkillButton, settleMs: 350 },
    mode: 'auto',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 95,
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'SKILL_ADD_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          anchor: AnchorId.SkillDialog,
          spotlightAnchor: AnchorId.SkillDialog,
          title: 'TUTORIAL.SKILL_ADD.WELCOME.TITLE',
          content: 'TUTORIAL.SKILL_ADD.WELCOME.CONTENT',
          placement: 'right',
          dialogContext: { ...DIALOG_CTX, popoverOutsideDialog: true },
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'name',
          anchor: AnchorId.SkillNameField,
          spotlightAnchor: AnchorId.SkillNameField,
          title: 'TUTORIAL.SKILL_ADD.NAME.TITLE',
          content: 'TUTORIAL.SKILL_ADD.NAME.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'departments',
          anchor: AnchorId.DepartmentsSelect,
          spotlightAnchor: AnchorId.DepartmentsSelect,
          title: 'TUTORIAL.SKILL_ADD.DEPARTMENTS.TITLE',
          content: 'TUTORIAL.SKILL_ADD.DEPARTMENTS.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="departments-select"]'),
        },
        {
          id: 'icon-search',
          // Spotlight auf das Suchfeld + zusaetzlich die Kategorie-Auswahl.
          anchor: AnchorId.IconSearchField,
          spotlightAnchor: AnchorId.IconSearchField,
          additionalSpotlightAnchors: [AnchorId.IconTypeSelect],
          title: 'TUTORIAL.SKILL_ADD.ICON_SEARCH.TITLE',
          content: 'TUTORIAL.SKILL_ADD.ICON_SEARCH.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="icon-search-field"]'),
        },
        {
          id: 'icon-grid',
          anchor: AnchorId.IconSelectionGrid,
          spotlightAnchor: AnchorId.IconSelectionGrid,
          title: 'TUTORIAL.SKILL_ADD.ICON_GRID.TITLE',
          content: 'TUTORIAL.SKILL_ADD.ICON_GRID.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="icon-selection-grid"]'),
        },
        {
          id: 'create',
          anchor: AnchorId.SaveSkillButton,
          spotlightAnchor: AnchorId.SaveSkillButton,
          title: 'TUTORIAL.SKILL_ADD.CREATE.TITLE',
          content: 'TUTORIAL.SKILL_ADD.CREATE.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
      ],
    },
  ],
};
